/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { useEffect, useState } from 'react';
import { Progress } from 'reactstrap';
import './styles.scss';
import SimpleTooltip from '../simple-tooltip';
import { AnimatedProgressBar } from './AnimatedProgressBar';
import NumberFormat from '@utils/number-format';

export enum AnimationVariant {
  None = '',
  Default = 'default',
  Animated = 'animated',
}

interface SurveyProgressProps {
  count: number;
  updated: number;
  verified: number;
  animationVariant?: AnimationVariant;
}

const SurveyProgress = (props: SurveyProgressProps) => {
  const { count, updated, verified, animationVariant } = props;
  const [variant, setVariant] = useState<AnimationVariant>(AnimationVariant.None);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (animationVariant) {
      timeout = setTimeout(() => setVariant(animationVariant), 100);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [animationVariant]);

  const getFormattedNumber = (value: number) => <NumberFormat value={value} decimalPlaces={0} emptyValue={' '} suffix='%' />;

  const roundedAnswered = Math.round((100 * updated) / count);
  const roundedVerified = Math.round((100 * verified) / count);
  const answeredPercent = getFormattedNumber(roundedAnswered);
  const verifiedPercent = getFormattedNumber(roundedVerified);

  const progressDetails = () => (
    <>
      <Progress bar max={count} value={updated} className='background-ThemeAccentExtradark'>
        <SimpleTooltip text={<span>Answered: {answeredPercent}</span>}>{answeredPercent}</SimpleTooltip>
      </Progress>
      <Progress bar max={count} value={verified} className='background-ThemeSuccessMedium'>
        <SimpleTooltip text={<span>Verified: {verifiedPercent}</span>}>{verifiedPercent}</SimpleTooltip>
      </Progress>
    </>
  );

  switch (animationVariant) {
    case AnimationVariant.Animated:
      return (
        <AnimatedProgressBar valueEnd={roundedAnswered + roundedVerified} variant={variant}>
          {progressDetails()}
        </AnimatedProgressBar>
      );
    case AnimationVariant.Default:
    case AnimationVariant.None:
    default:
      return <Progress multi>{progressDetails()}</Progress>;
  }
};

export default SurveyProgress;
